import SectionMessage from '@atlaskit/section-message';

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import styled from 'styled-components';

import config from 'appconfig';
import { ProductGroupKeys, SupportProductKeys } from 'data/products/keys';
import t from 'modules/i18n/intl';
import SupportProduct from 'model/SupportProduct';
import SupportProductGroup from 'model/SupportProductGroup';
import { Dimensions, Font } from 'components/visuals';
import { State } from 'model/State';

type PropsType = {
  supportKeyName: string;
  descriptionKeyName: string;
  productKey?: string;
};

export const NewNotificationSectionWrapper = styled.div`
  width: 480px;
  font-family: ${Font.family};
  margin-top: ${Dimensions.unit * 3}px;
`;

const cloudAccessLink = `${config.wacUrl}/enterprise/cloud/access`;
const moreLink =
  'https://confluence.atlassian.com/display/Support/Priority+Support+Server+Offering+Details';

const EndOfSupportLink = (chunks) => (
  <>
    <br />
    <a
      href="https://confluence.atlassian.com/migration/assess/journey-to-cloud"
      target="_blank"
      rel="noreferrer"
    >
      {chunks}
    </a>
  </>
);

const LearnMoreLink = (chunks) => <a href={moreLink}>{chunks}</a>;

const FormattedDescriptionMessage = ({
  supportKeyName,
  descriptionKeyName,
  productKey,
}: PropsType) => {
  const isNewDescription = descriptionKeyName === 'longDescription';
  const isServerEndOfSupportBanner = productKey === SupportProductKeys.PRIORITY_SUPPORT_SERVER;

  return isNewDescription ? (
    <>
      {isServerEndOfSupportBanner && (
        <>
          <NewNotificationSectionWrapper>
            <SectionMessage appearance="information">
              {t(
                {
                  id: 'purchase.server-product.priority-support',
                  defaultMessage:
                    'Priority Support for Server may only be purchased to cover the maintenance period until February 15th 2024, when our Server products will reach their <link>End of Support</link>. The license price will be adjusted according to the period covered.',
                },
                {
                  link: EndOfSupportLink,
                },
              )}
            </SectionMessage>
          </NewNotificationSectionWrapper>
          <br />
        </>
      )}
      {t(
        {
          id: 'products.priority-support.server.longDescription-p2',
          defaultMessage:
            'For Data Center products, Priority Support is included with each license (except for Bitbucket and Bamboo where it’s dependent on user tier). <link>Learn more about Priority Support</link>',
        },
        {
          link: LearnMoreLink,
        },
      )}
    </>
  ) : (
    <FormattedMessage id={`products.priority-support.${supportKeyName}.${descriptionKeyName}`} />
  );
};

const DescriptionComponent = connect((state: State) => ({
  supportKeyName: 'server',
  productKey: state.configure?.product.productKey,
}))(FormattedDescriptionMessage);

const DeploymentsNoteComponent = () => {
  return (
    <span>
      {t('purchase.cloud-product.priority-support.guard')}
      &nbsp;
      <a href={cloudAccessLink} target="_blank" rel="noreferrer">
        <strong>{t('purchase-learn-more-about-guard')}</strong>
      </a>
    </span>
  );
};

const props = {
  groupKey: ProductGroupKeys.LEGACY_PRIORITY_SUPPORT,
  tagLine: t('products.priority-support.server.tagline'),
  description: <DescriptionComponent descriptionKeyName="description" />,
  name: t('products.priority-support.server.name'),
  longDescription: <DescriptionComponent descriptionKeyName="longDescription" />,
  deploymentsNote: <DeploymentsNoteComponent />,
  moreLink,
};

export const prioritySupportServerBitbucket = new SupportProduct({
  ...props,
  groupKey: ProductGroupKeys.PRIORITY_SUPPORT_BITBUCKET,
  key: SupportProductKeys.PRIORITY_SUPPORT_SERVER,
});

export const prioritySupportServerBamboo = new SupportProduct({
  ...props,
  groupKey: ProductGroupKeys.PRIORITY_SUPPORT_BAMBOO,
  key: SupportProductKeys.PRIORITY_SUPPORT_SERVER,
});

export const prioritySupportDatacenterBitbucket = new SupportProduct({
  ...props,
  groupKey: ProductGroupKeys.PRIORITY_SUPPORT_BITBUCKET,
  key: SupportProductKeys.PRIORITY_SUPPORT_DATA_CENTER_BITBUCKET,
  enableProductSelect: true,
});

export const prioritySupportDatacenterBamboo = new SupportProduct({
  ...props,
  groupKey: ProductGroupKeys.PRIORITY_SUPPORT_BAMBOO,
  key: SupportProductKeys.PRIORITY_SUPPORT_DATA_CENTER_BAMBOO,
  enableProductSelect: true,
});

export const prioritySupportProductGroupWithBitbucket = new SupportProductGroup(
  prioritySupportServerBitbucket,
  prioritySupportDatacenterBitbucket,
);

export const prioritySupportProductGroupWithBamboo = new SupportProductGroup(
  prioritySupportServerBamboo,
  prioritySupportDatacenterBamboo,
);
